import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useIssueState} from '@github-ui/issue-pr-state'
import {CrossReferenceIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {ConnectedEvent$key} from './__generated__/ConnectedEvent.graphql'
import styles from './ConnectedEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

type ConnectedEventProps = {
  queryRef: ConnectedEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
}

export function ConnectedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
}: ConnectedEventProps): React.ReactElement {
  const {actor, createdAt, subject, databaseId} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ConnectedEvent on ConnectedEvent {
        databaseId
        actor {
          ...TimelineRowEventActor
        }
        createdAt
        subject {
          ... on PullRequest {
            title
            url
            number
            state
            isDraft
            isInMergeQueue
            repository {
              name
              owner {
                login
              }
            }
          }
        }
      }
    `,
    queryRef,
  )

  const {title, number, url, isDraft, isInMergeQueue, state, repository} = subject || {}

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {sourceIcon} = useIssueState({state})

  const PullStateIcon = sourceIcon('PullRequest', isDraft, isInMergeQueue)
  const highlighted = String(databaseId) === highlightedEventId

  const hovercardAttributes =
    repository && number
      ? getHovercardAttributesForType('pull_request', {
          owner: repository.owner.login,
          repo: repository.name,
          pullRequestNumber: number,
        })
      : {}

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={CrossReferenceIcon}
    >
      <TimelineRow.Main>
        {LABELS.timeline.linkedAClosingPR}
        {/* eslint-disable-next-line react-hooks/static-components */}
        <PullStateIcon className="ml-1 mr-1" />
        <Link href={url} target="_blank" {...hovercardAttributes} inline className={styles.linkedPullRequestLink}>
          {`${title} #${number}`}
        </Link>
      </TimelineRow.Main>
    </TimelineRow>
  )
}
